/**
 * Luxe · Three.js 3D — Coordinate Adapter (Phase 2B-1).
 *
 * ONE deterministic adapter between canonical DesignScene coordinates
 * (mm, Y-up-from-FFL) and Three.js world coordinates (metres, Y-up).
 *
 * Scattering /1000 constants across builders would make bug hunts a
 * nightmare; every builder consumes this module instead. Also keeps
 * the door open to a future room-origin translation without touching
 * every builder.
 *
 * Conventions:
 *   • Canonical mm → Three.js m: divide by 1000.
 *   • Canonical Y is FLOOR-up (matches DesignScene) — same as Three.js.
 *   • Canonical Z is projection toward the room (positive Z).
 *   • Wall lies on the XY plane at Z = 0 (looking down -Z).
 */
export const MM_PER_M = 1000;

export const mmToM = (mm) => (Number(mm) || 0) / MM_PER_M;

/** Return {x, y, z} in metres from canonical mm coordinates. */
export const posToWorld = (xMm, yMm, zMm) => ({
  x: mmToM(xMm),
  y: mmToM(yMm),
  z: mmToM(zMm || 0),
});

/** Return {x, y, z} extents in metres from canonical mm dimensions. */
export const sizeToWorld = (widthMm, heightMm, depthMm) => ({
  x: mmToM(widthMm),
  y: mmToM(heightMm),
  z: mmToM(depthMm),
});

/** Convert a canonical mm distance to Three.js m. */
export const distToWorld = (mm) => mmToM(mm);

/** Compute an item's world-space centre (Three.js uses object centres). */
export const centreToWorld = (item) => ({
  x: mmToM((Number(item.x) || 0) + (Number(item.width) || 0)  / 2),
  y: mmToM((Number(item.y) || 0) + (Number(item.height) || 0) / 2),
  z: mmToM((Number(item.z) || 0) + (Number(item.depth) || 0)  / 2),
});
